/* global rateCardPickerUrl, rateCardNameInputId, rateCardPriceInputId, rateCardSelectedRoleId */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import { formatToFixedMoneyWithLocale } from 'Helpers/formatService';

/**
 * The rate card affordance on the Add Expense form: a chip inside the Expense
 * name field, the item list it opens, and the fields a selection fills in.
 *
 * It drives the surrounding Blade inputs directly rather than owning them.
 * Name, Price and Quantity predate this by years and are read by the form post,
 * the pricing script and the quantity island alike — re-rendering them here
 * would mean owning all of that. What it does own is the chip, the list, and a
 * hidden `rate_card_role_id` that tells the server a rate card item was chosen;
 * the price the server books is re-derived from that id, never from the field
 * this fills in.
 *
 * The quantity island is a separate React root, so the two talk through window
 * events — the same idiom the quantity-link picker already uses.
 */

/** Fired when an item is chosen, so the quantity island can lock its unit. */
const SELECTED_EVENT = 'ratecard:selected';

/** Fired on ✕ or Esc, so the same island unlocks and manual entry resumes. */
const CLEARED_EVENT = 'ratecard:cleared';

function ChevronDown() {
    return (
        <svg className="tw-h-2 tw-w-2" viewBox="0 0 10 6" fill="none" aria-hidden="true">
            <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function CrossMark() {
    return (
        <svg className="tw-h-2 tw-w-2" viewBox="0 0 10 10" fill="none" aria-hidden="true">
            <path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    );
}

export default function ExpenseRateCardPicker({ pickerUrl, nameInputId, priceInputId, selectedRoleId }) {
    const [versions, setVersions] = useState([]);
    const [currency, setCurrency] = useState(null);
    const [versionId, setVersionId] = useState(null);
    const [open, setOpen] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [failed, setFailed] = useState(false);
    const [filter, setFilter] = useState('');
    const [selected, setSelected] = useState(null);

    const panelRef = useRef(null);
    // What the person had typed before the list opened, so ✕ and Esc can give it
    // back rather than blanking the field they were part-way through.
    const typedBeforeOpenRef = useRef('');
    // The same for Price. It starts empty and stays empty until the list is
    // opened over a manual line, which is what ✕ must hand back after a
    // selection: a price the rate card supplied is not one to keep on a line
    // the person has just taken back to manual.
    const typedPriceBeforeOpenRef = useRef('');
    // One fetch at a time. A failure is not remembered, so reopening the list
    // retries — a dropped connection must not disable the rate card for the
    // life of the page, leaving the person to reload to get it back.
    const inFlightRef = useRef(false);

    const listboxId = `${nameInputId}-rate-card-listbox`;

    const nameInput = () => document.getElementById(nameInputId);
    const priceInput = () => document.getElementById(priceInputId);

    // The fetched versions, kept alongside the state so load() can resolve with
    // them — re-hydration below needs the list in the same tick it arrives,
    // before React has re-rendered.
    const versionsRef = useRef([]);

    const load = () => {
        if (loaded || inFlightRef.current) return Promise.resolve(versionsRef.current);

        inFlightRef.current = true;
        setFailed(false);

        return axios
            .get(pickerUrl)
            .then(({ data }) => {
                const list = data.data || [];

                versionsRef.current = list;
                setVersions(list);
                setCurrency(data.meta?.currency || null);
                setVersionId(data.meta?.current_version_id ?? list[0]?.id ?? null);
                setLoaded(true);

                return list;
            })
            .catch(() => {
                setFailed(true);

                return [];
            })
            .finally(() => {
                inFlightRef.current = false;
            });
    };

    const activeVersion = useMemo(() => versions.find((version) => version.id === versionId) || versions[0] || null, [versions, versionId]);

    const items = useMemo(() => {
        const roles = activeVersion?.roles || [];
        const needle = filter.trim().toLowerCase();
        if (!needle) return roles;

        return roles.filter((role) => `${role.name} ${role.unit || ''}`.toLowerCase().includes(needle));
    }, [activeVersion, filter]);

    const openList = () => {
        const input = nameInput();

        // Only snapshot while the line is still manual. Reopening the list over
        // a chosen item would otherwise record that item's own name and rate as
        // the thing to revert to, and ✕ would hand back the very figures it is
        // meant to remove.
        if (!selected) {
            typedBeforeOpenRef.current = input ? input.value : '';
            typedPriceBeforeOpenRef.current = priceInput()?.value ?? '';
        }

        setFilter(input ? input.value : '');
        setOpen(true);
        load();
        input?.focus();
    };

    /**
     * Back to a manual line as it stood before the list opened: the typed name
     * and the typed price both handed back, and the price unlocked.
     *
     * Unlocking the price without restoring it leaves the expense priced at an
     * item the person has just rejected — a figure they never typed, on a line
     * that no longer carries the rate card id explaining it.
     */
    const revertToManual = () => {
        setOpen(false);
        setFilter('');
        setSelected(null);

        const input = nameInput();
        if (input && selected && input.value === selected.name) {
            input.value = typedBeforeOpenRef.current;
        }

        const price = priceInput();
        if (price) {
            // Guarded the same way as the name: only a field still holding the
            // rate card's own figure is the picker's to take back.
            if (selected && String(price.value) === String(selected.cost_rate)) {
                price.value = typedPriceBeforeOpenRef.current;
                price.dispatchEvent(new Event('input', { bubbles: true }));
                price.dispatchEvent(new Event('change', { bubbles: true }));
            }

            price.readOnly = false;
            price.classList.remove('inbud-rate-card-locked');
        }

        window.dispatchEvent(new CustomEvent(CLEARED_EVENT));
    };

    /**
     * The item's rate into the price field, locked. Shared by a fresh selection
     * and by re-hydration after a failed submit; the server re-derives the
     * figure from the id either way, so this is what the person sees rather
     * than what they are charged.
     */
    const lockPriceTo = (role) => {
        const price = priceInput();
        if (!price) return;

        price.value = role.cost_rate;
        price.readOnly = true;
        price.classList.add('inbud-rate-card-locked');
        price.dispatchEvent(new Event('input', { bubbles: true }));
        price.dispatchEvent(new Event('change', { bubbles: true }));
    };

    const announceSelection = (role) => {
        window.dispatchEvent(new CustomEvent(SELECTED_EVENT, { detail: { unit: role.unit || '', name: role.name, rate: role.cost_rate } }));
    };

    const choose = (role) => {
        setSelected(role);
        setOpen(false);
        setFilter('');

        const input = nameInput();
        if (input) {
            input.value = role.name;
            // The pricing script and any listener bound to the field react to a
            // typed change; a value set from script raises neither on its own.
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
        }

        lockPriceTo(role);
        announceSelection(role);
    };

    /**
     * A rejected submit comes back through back()->withErrors(), which re-renders
     * the form from old(). The name and price the rate card filled in survive
     * that; this component's state does not, so without re-hydrating, the hidden
     * rate_card_role_id is gone and the resubmit silently becomes a manual line
     * priced at the rate card's figure.
     *
     * The name is deliberately left alone — old() already holds it, and it may
     * be a rename the person made after choosing.
     */
    useEffect(() => {
        if (!selectedRoleId) return;

        load().then((list) => {
            let match = null;

            (list || []).some((version) => {
                const role = (version.roles || []).find((candidate) => String(candidate.id) === String(selectedRoleId));
                if (!role) return false;

                match = { version, role };

                return true;
            });

            // An item archived between the two submits simply does not come
            // back; the line reverts to manual rather than booking against
            // something the picker would no longer offer.
            if (!match) return;

            // Seeded so ✕ hands back what old() re-rendered rather than the
            // empty string this ref starts at — the list was never opened, so
            // nothing else has set it. The price ref is deliberately left empty:
            // old() re-rendered the rate card's own figure into a read-only
            // field, so there is no typed price to give back.
            typedBeforeOpenRef.current = nameInput()?.value ?? '';

            setSelected(match.role);
            setVersionId(match.version.id);
            lockPriceTo(match.role);
            announceSelection(match.role);
        });
    }, []);

    // ↓ in the name field opens the list, typing filters it, and Esc leaves.
    // Bound to the Blade input rather than a React-owned one, because that is
    // the field the ticket puts the chip inside.
    useEffect(() => {
        const input = nameInput();
        if (!input) return undefined;

        const onKeyDown = (e) => {
            if (e.key === 'ArrowDown' && !open) {
                e.preventDefault();
                openList();

                return;
            }

            if ((e.key === 'Escape' || e.key === 'Esc') && (open || selected)) {
                e.preventDefault();
                revertToManual();
            }
        };

        const onInput = () => {
            if (open) setFilter(input.value);
        };

        input.addEventListener('keydown', onKeyDown);
        input.addEventListener('input', onInput);

        return () => {
            input.removeEventListener('keydown', onKeyDown);
            input.removeEventListener('input', onInput);
        };
    }, [open, selected, loaded, failed]);

    // A click anywhere else closes the list. It does not revert: leaving the
    // list without choosing is not the same as saying "make this manual".
    useEffect(() => {
        if (!open) return undefined;

        const onDocumentClick = (e) => {
            if (panelRef.current?.contains(e.target)) return;
            if (e.target === nameInput()) return;

            setOpen(false);
        };

        document.addEventListener('click', onDocumentClick);

        return () => document.removeEventListener('click', onDocumentClick);
    }, [open]);

    // Two decimals, always: formatToFixedMoneyWithLocale defaults to none, and an
    // item priced 250.50 offered as "251" shows one number and books another.
    // The quick-add panel in expenditureTable.js renders a rate the same way.
    const rate = (value) => (currency?.symbol ? `${currency.symbol}${formatToFixedMoneyWithLocale(value, 2)}` : formatToFixedMoneyWithLocale(value, 2));

    /**
     * Keyboard on an option: choose it, step to a sibling, or leave. Focus is
     * moved through the DOM rather than tracked in state — the list is rebuilt
     * wholesale on every filter keystroke, so a remembered index would point at
     * a different item by the time it was read.
     */
    const onOptionKeyDown = (e, role) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            choose(role);

            return;
        }

        if (e.key === 'Escape' || e.key === 'Esc') {
            e.preventDefault();
            revertToManual();
            nameInput()?.focus();

            return;
        }

        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;

        e.preventDefault();

        const next = e.key === 'ArrowDown' ? e.currentTarget.nextElementSibling : e.currentTarget.previousElementSibling;

        if (next) {
            next.focus();

            return;
        }

        // Off the top of the list is back to the field, where typing filters.
        if (e.key === 'ArrowUp') nameInput()?.focus();
    };

    return (
        <>
            {selected && <input type="hidden" name="rate_card_role_id" value={selected.id} />}

            <button
                type="button"
                className="inbud-rate-card-chip"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={open ? listboxId : undefined}
                aria-label={selected ? $t('Remove the rate card item and go back to a manual expense') : $t('Choose an item from the rate card')}
                title={selected ? $t('Back to a manual expense') : $t('Choose an item from the rate card')}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    if (selected) {
                        revertToManual();

                        return;
                    }

                    if (open) {
                        setOpen(false);

                        return;
                    }

                    openList();
                }}
            >
                <span>{$t('RC')}</span>
                {selected ? <CrossMark /> : <ChevronDown />}
            </button>

            {open && (
                // The listbox is the item list ALONE. The version switcher, the
                // status messages and the hint are siblings of it, not children:
                // a listbox whose children are not all options is invalid, and a
                // screen reader announcing "5 items" over a list of three items
                // and two buttons is worse than no role at all.
                <div ref={panelRef} className="inbud-rate-card-panel">
                    {versions.length > 1 && (
                        <div className="inbud-rate-card-panel__versions" role="group" aria-label={$t('Rate card version')}>
                            {versions.map((version) => (
                                <button
                                    key={version.id}
                                    type="button"
                                    aria-pressed={version.id === activeVersion?.id}
                                    className={`inbud-rate-card-panel__version${version.id === activeVersion?.id ? ' is-active' : ''}`}
                                    onClick={() => setVersionId(version.id)}
                                >
                                    {version.name}
                                </button>
                            ))}
                        </div>
                    )}

                    {failed && (
                        <div className="inbud-rate-card-panel__empty" role="alert">
                            {$t('The rate card could not be loaded.')}
                        </div>
                    )}

                    {!failed && !loaded && <div className="inbud-rate-card-panel__empty">{$t('Loading…')}</div>}

                    {loaded && items.length === 0 && (
                        <div className="inbud-rate-card-panel__empty">{filter.trim() ? $t('No matching rate card items') : $t('No rate card items yet')}</div>
                    )}

                    {loaded && items.length > 0 && (
                        <div id={listboxId} role="listbox" aria-label={$t('Rate card items')}>
                            {items.map((role) => (
                                // A div rather than a button: a button carrying role="option"
                                // discards its own semantics and keeps its keyboard behaviour,
                                // which is the confusing half of both. Options stay tabbable
                                // and take the arrow keys below, so this is reachable without
                                // a mouse.
                                <div
                                    key={role.id}
                                    role="option"
                                    tabIndex={0}
                                    aria-selected={selected?.id === role.id}
                                    className="inbud-rate-card-panel__item"
                                    onClick={() => choose(role)}
                                    onKeyDown={(e) => onOptionKeyDown(e, role)}
                                >
                                    <span className="inbud-rate-card-panel__item-name">
                                        {role.name}
                                        {role.unit ? <span className="inbud-rate-card-panel__item-unit"> · {role.unit}</span> : null}
                                    </span>
                                    <span className="inbud-rate-card-panel__item-rate">{rate(role.cost_rate)}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="inbud-rate-card-panel__hint">{$t('Selection fills the name, price and unit · ✕ or Esc keeps what you typed')}</div>
                </div>
            )}
        </>
    );
}

if (document.getElementById('expenseRateCardPicker')) {
    const root = document.getElementById('expenseRateCardPicker');

    ReactDOM.render(
        <App>
            <ExpenseRateCardPicker
                pickerUrl={rateCardPickerUrl}
                nameInputId={rateCardNameInputId}
                priceInputId={rateCardPriceInputId}
                selectedRoleId={typeof rateCardSelectedRoleId !== 'undefined' ? rateCardSelectedRoleId : null}
            />
        </App>,
        root,
    );
}
